((root, factory) => {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else if (root.document) api.start(root);
})(typeof window === "undefined" ? globalThis : window, () => {
  const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const STORAGE_KEY = "kayoz.fang-gift.v1";
  const GIFT_PROPERTY = "Free gift";
  const CLAIM_PROPERTY = "_kayoz_fang_claim";
  const NOTE_START = "[KAYOZ FANG GIFT]";
  const NOTE_END = "[/KAYOZ FANG GIFT]";
  const GIFT_VALUE = "Fang Necklace ×1 — packed with your order";
  const ATTRS = ["fang_gift_claim", "fang_gift_issued_at", "fang_gift_expires_at", "fang_gift_status", "fang_gift_declined"];

  function validateClaim(value, now = Date.now()) {
    if (!value || value.version !== 1 || !/^FANG-[a-zA-Z0-9-]{16,64}$/.test(value.id || "")) return null;
    const issued = Date.parse(value.issued_at);
    const expires = Date.parse(value.expires_at);
    if (!Number.isFinite(issued) || !Number.isFinite(expires) || issued > now + 300000 || expires - issued !== WINDOW_MS) return null;
    return { version: 1, id: value.id, issued_at: new Date(issued).toISOString(), expires_at: new Date(expires).toISOString(), identity: value.identity || "", recorded: value.recorded === true, declined: value.declined === true };
  }

  function createClaim(id, now = Date.now()) {
    return validateClaim({ version: 1, id, issued_at: new Date(now).toISOString(), expires_at: new Date(now + WINDOW_MS).toISOString() }, now);
  }

  function claimFromAttributes(attributes = {}, now = Date.now()) {
    return validateClaim({ version: 1, id: attributes.fang_gift_claim, issued_at: attributes.fang_gift_issued_at, expires_at: attributes.fang_gift_expires_at, declined: attributes.fang_gift_declined === "true" }, now);
  }

  function restoreClaim(current, incoming) {
    if (!incoming) return current;
    if (!current) return incoming;
    // A return link must not undo the shopper's choice or restart their window.
    if (current.id === incoming.id) return current;
    return Date.parse(incoming.issued_at) < Date.parse(current.issued_at) ? incoming : current;
  }

  function claimFromUrl(url, now = Date.now()) {
    const params = new URL(url).searchParams;
    return validateClaim({ version: 1, id: params.get("fang_claim"), issued_at: params.get("fang_issued"), expires_at: params.get("fang_expires") }, now);
  }

  function claimUrl(claim, origin) {
    const url = new URL("/collections/all", origin);
    url.searchParams.set("fang_gift", "claim");
    url.searchParams.set("fang_claim", claim.id);
    url.searchParams.set("fang_issued", claim.issued_at);
    url.searchParams.set("fang_expires", claim.expires_at);
    return url.href;
  }

  function submittedEmail(detail, formId) {
    const email = detail?.metaData?.email || detail?.metaData?.$email;
    return detail?.formId === formId && ["submit", "stepSubmit"].includes(detail.type) &&
      typeof email === "string" && email.includes("@") ? email.trim() : null;
  }

  function eligible(item, excludedProducts) {
    return item.quantity > 0 && item.requires_shipping === true && item.gift_card !== true &&
      Number(item.final_line_price) > 0 && !excludedProducts.map(String).includes(String(item.product_id));
  }

  function statusFor(cart, claim, config, now = Date.now()) {
    if (config.redeemed) return claim ? "redeemed" : "none";
    if (!claim) return "none";
    if (Date.parse(claim.expires_at) <= now) return "expired";
    if (!config.available) return "unavailable";
    if (!(cart.total_price > 0 && cart.items.some(item => eligible(item, config.excludedProducts)))) return "pending";
    if (claim.declined) return "declined";
    return "included";
  }

  function preferenceFor(state, confirmedState = "none") {
    const ready = ["included", "declined"].includes(state);
    const waiting = ["updating", "error"].includes(state) && ["included", "declined"].includes(confirmedState);
    return { hidden: !ready && !waiting, checked: state === "included", disabled: !ready };
  }

  function giftNote(note, claim, included) {
    const cleaned = String(note || "").replace(/(?:\n\n)?\[KAYOZ FANG GIFT\][\s\S]*?\[\/KAYOZ FANG GIFT\]/g, "");
    if (!included) return cleaned;
    const block = `${NOTE_START}\nPack 1 complimentary Fang Necklace after checking eligibility.\nClaim: ${claim.id}\nClaimed: ${claim.issued_at}\nExpires: ${claim.expires_at}\nOne gift per subscriber. Verify paid non-Fang merchandise and prior redemption.\n${NOTE_END}`;
    return cleaned ? `${cleaned}\n\n${block}` : block;
  }

  function propertyEqual(a = {}, b = {}) {
    const normalize = value => Object.entries(value).filter(([, v]) => v !== "" && v != null).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
  }

  function nextChange(cart, claim, config, now = Date.now()) {
    const status = statusFor(cart, claim, config, now);
    const candidates = status === "included" ? cart.items.filter(item => eligible(item, config.excludedProducts)) : [];
    const target = candidates.find(item => item.properties?.[CLAIM_PROPERTY] === claim.id) || candidates[0];
    for (const item of cart.items) {
      const properties = { ...item.properties };
      const owned = Boolean(properties[CLAIM_PROPERTY]);
      if (item === target) {
        properties[GIFT_PROPERTY] = GIFT_VALUE;
        properties[CLAIM_PROPERTY] = claim.id;
      } else if (owned) {
        properties[GIFT_PROPERTY] = "";
        properties[CLAIM_PROPERTY] = "";
      }
      if (!propertyEqual(properties, item.properties)) return { path: "cart/change.js", body: { id: item.key, quantity: item.quantity, properties } };
    }
    const attributes = {
      fang_gift_claim: claim?.id || "",
      fang_gift_issued_at: claim?.issued_at || "",
      fang_gift_expires_at: claim?.expires_at || "",
      fang_gift_status: claim ? status : "",
      fang_gift_declined: claim ? String(claim.declined === true) : "",
    };
    const note = giftNote(cart.note, claim, status === "included");
    if (ATTRS.some(key => (cart.attributes?.[key] || "") !== attributes[key]) || (cart.note || "") !== note) {
      return { path: "cart/update.js", body: { attributes, note } };
    }
    return null;
  }

  async function reconcile(request, claim, config, now = () => Date.now()) {
    let cart = await request("cart.js");
    let changed = false;
    for (let attempt = 0; attempt < cart.items.length + 8; attempt += 1) {
      const change = nextChange(cart, claim, config, now());
      if (!change) return { cart, changed, status: statusFor(cart, claim, config, now()) };
      await request(change.path, change.body);
      changed = true;
      cart = await request("cart.js");
    }
    throw new Error("Gift markers could not be confirmed.");
  }

  function start(win) {
    const doc = win.document;
    const configElement = doc.querySelector("[data-kayoz-fang-config]");
    if (!configElement || win.KayozFangGiftLoaded) return;
    win.KayozFangGiftLoaded = true;
    const config = JSON.parse(configElement.textContent);
    const nativeFetch = win.fetch.bind(win);
    const cartRoot = win.Shopify?.routes?.root || "/";
    const pendingMutations = new Set();
    let claim = null;
    let state = "none";
    let confirmedState = "none";
    let scheduled = null;
    let currentSync = null;
    let again = false;
    let allowSubmit = false;
    let synchronizing = false;
    let lastMutation = 0;
    let expiryTimer;
    let claimEmail = "";
    let recording = null;
    let toggleFocus = null;
    let cartTransaction = Promise.resolve();
    const withCartLock = action => {
      const task = cartTransaction.then(action, action);
      cartTransaction = task.catch(() => {});
      return task;
    };

    const readStored = () => {
      try { return validateClaim(JSON.parse(win.localStorage.getItem(STORAGE_KEY))); } catch { return null; }
    };
    const saveClaim = value => {
      claim = value;
      try {
        if (value) win.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
        else win.localStorage.removeItem(STORAGE_KEY);
      } catch {}
      win.clearTimeout(expiryTimer);
      if (value && Date.parse(value.expires_at) > Date.now()) expiryTimer = win.setTimeout(() => schedule(), Math.min(Date.parse(value.expires_at) - Date.now() + 100, 2147483647));
    };
    saveClaim(readStored());

    const request = async (path, body) => {
      const response = await nativeFetch(cartRoot + path, {
        method: body ? "POST" : "GET", credentials: "same-origin", cache: "no-store",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: AbortSignal.timeout(12000),
      });
      if (!response.ok) throw new Error(`Cart request failed (${response.status}).`);
      return response.json();
    };

    function render() {
      const choice = preferenceFor(state, confirmedState);
      doc.documentElement.classList.toggle("kayoz-fang-active", Boolean(claim) && !["expired", "redeemed", "unavailable", "declined"].includes(state));
      doc.documentElement.classList.toggle("kayoz-fang-busy", synchronizing);
      doc.querySelectorAll("[data-kayoz-fang-card]").forEach(card => {
        card.hidden = !claim;
        card.dataset.state = state;
        card.setAttribute("aria-busy", String(state === "updating"));
        const title = card.querySelector("[data-fang-status]");
        const message = card.querySelector("[data-fang-message]");
        const copy = config.strings[state === "error" && claim?.declined ? "decline_error" : state];
        if (title) title.textContent = copy?.title || "";
        if (message) message.textContent = copy?.message || "";
        const preference = card.querySelector("[data-fang-preference]");
        if (preference) preference.hidden = !claim || choice.hidden;
        const toggle = card.querySelector("[data-fang-toggle]");
        if (toggle) {
          toggle.checked = choice.checked;
          toggle.disabled = choice.disabled;
        }
        const retry = card.querySelector("[data-fang-retry]");
        if (retry) retry.hidden = state !== "error";
      });
      doc.querySelectorAll('[name="note"]').forEach(input => {
        if (input !== doc.activeElement && input.dataset.fangNote !== undefined) input.value = input.dataset.fangNote;
      });
      if (toggleFocus && !choice.disabled) {
        if (doc.activeElement === doc.body) {
          const replacement = toggleFocus.section?.querySelector("[data-fang-toggle]");
          if (replacement?.getClientRects().length) replacement.focus({ preventScroll: true });
        }
        toggleFocus = null;
      }
    }

    async function refreshCartViews(cart) {
      if (doc.activeElement?.matches?.("[data-fang-toggle]")) {
        toggleFocus = { element: doc.activeElement, section: doc.activeElement.closest("[data-section-type]") };
      }
      doc.querySelectorAll('[name="note"]').forEach(input => {
        input.dataset.fangNote = cart.note || "";
        if (input !== doc.activeElement) input.value = cart.note || "";
      });
      const fullCart = doc.querySelector('[data-section-type="cart"]');
      if (fullCart) {
        const response = await nativeFetch(`${cartRoot}cart?section_id=${encodeURIComponent(fullCart.dataset.sectionId)}`);
        if (!response.ok) throw new Error("Cart display could not be refreshed.");
        const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
        if (Boolean(fullCart.querySelector("[js-cart-items]")) !== Boolean(parsed.querySelector("[js-cart-items]"))) {
          const target = fullCart.querySelector(".cart__container");
          const source = parsed.querySelector(".cart__container");
          if (target && source) target.innerHTML = source.innerHTML;
        }
        const selectors = ["[js-cart-items]", ".cart__footer-subtotal-wrapper"];
        for (const selector of selectors) {
          const target = fullCart.querySelector(selector);
          const source = parsed.querySelector(selector);
          if (target && source) target.innerHTML = source.innerHTML;
        }
      }
      doc.dispatchEvent(new CustomEvent("apps:product-added-to-cart", { detail: { source: "fang-gift" } }));
    }

    async function sync() {
      if (currentSync) { again = true; return currentSync; }
      currentSync = (async () => {
        do {
          again = false;
          await Promise.allSettled([...pendingMutations]);
          const revision = lastMutation;
          synchronizing = true;
          if (claim) state = "updating";
          render();
          try {
            // Email delivery must never hold up a cart or checkout during an SDK outage.
            if (claimEmail && claim && !claim.recorded) void recordClaim(claimEmail);
            const result = await withCartLock(async () => {
              const existing = await request("cart.js");
              if (!claim) saveClaim(claimFromAttributes(existing.attributes));
              return reconcile(request, claim, config);
            });
            if (revision !== lastMutation || pendingMutations.size) { again = true; continue; }
            if (result.changed) await refreshCartViews(result.cart);
            state = result.status;
            confirmedState = result.status;
          } catch (error) {
            state = claim ? "error" : "none";
            win.console.warn("Fang gift: cart confirmation unavailable.");
          } finally {
            synchronizing = false;
            render();
          }
        } while (again);
        return state !== "error";
      })().finally(() => { currentSync = null; });
      return currentSync;
    }

    function schedule() {
      win.clearTimeout(scheduled);
      scheduled = win.setTimeout(sync, 120);
    }

    win.fetch = function(input, options) {
      let mutation = false;
      try {
        const url = new URL(typeof input === "string" || input instanceof URL ? input : input.url, win.location.href);
        const method = options?.method || input?.method || "GET";
        mutation = url.origin === win.location.origin && method.toUpperCase() !== "GET" && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
      } catch {}
      const promise = mutation ? withCartLock(() => nativeFetch(input, options)) : nativeFetch(input, options);
      if (mutation) {
        lastMutation += 1;
        if (claim) { state = "updating"; render(); }
        pendingMutations.add(promise);
        const finish = () => { pendingMutations.delete(promise); schedule(); };
        promise.then(finish, finish);
      }
      return promise;
    };

    async function recordClaim(email) {
      if (!claim || claim.recorded) return;
      if (recording) return recording;
      const current = claim;
      const properties = { $event_id: current.id, claim_id: current.id, issued_at: current.issued_at, expires_at: current.expires_at, claim_url: claimUrl(current, win.location.origin), form_id: config.formId, promotion: "fang-gift-v1", test: config.preview };
      recording = (async () => { try {
        if (typeof win.klaviyo?.identify !== "function" || typeof win.klaviyo?.track !== "function") throw new Error("Klaviyo is not ready.");
        let timeout;
        const track = async () => {
          if (config.preview) win.console.info("Fang gift tracking QA: identifying subscriber", JSON.stringify({
            analyticsAllowed: win.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.(),
            marketingAllowed: win.Shopify?.customerPrivacy?.marketingAllowed?.(),
          }));
          await win.klaviyo.identify({ email });
          if (config.preview) win.console.info("Fang gift tracking QA: recording claim");
          const accepted = await win.klaviyo.track("Fang Gift Claimed", properties);
          if (config.preview) win.console.info("Fang gift tracking QA: claim accepted", accepted === true);
          return accepted;
        };
        let accepted;
        try {
          accepted = await Promise.race([track(), new Promise((_, reject) => {
            timeout = win.setTimeout(() => reject(new Error("Klaviyo confirmation timed out.")), 12000);
          })]);
        } finally { win.clearTimeout(timeout); }
        if (accepted === true && claim?.id === current.id) saveClaim({ ...claim, recorded: true });
        else if (accepted !== true) win.console.warn("Fang gift: claim recording needs a retry.");
      } catch (error) { win.console.warn("Fang gift: claim recording unavailable.", config.preview ? String(error) : ""); }
      })().finally(() => { recording = null; });
      return recording;
    }

    win.addEventListener("klaviyoForms", async event => {
      const detail = event.detail || {};
      // Existing Klaviyo forms emit $email; newer examples use email.
      const email = submittedEmail(detail, config.formId);
      if (config.preview && detail.formId === config.formId) win.console.info("Fang gift form QA:", detail.type, Object.keys(detail.metaData || {}).join(","));
      if (!email || config.redeemed) return;
      const digest = await win.crypto.subtle.digest("SHA-256", new TextEncoder().encode(email.trim().toLowerCase()));
      const identity = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
      if (!claim || (claim.identity && claim.identity !== identity)) saveClaim({ ...createClaim(`FANG-${win.crypto.randomUUID()}`), identity });
      else if (!claim.identity) saveClaim({ ...claim, identity });
      claimEmail = email.trim();
      await sync();
    });

    function openCart() {
      const trigger = doc.querySelector("[data-js-cart-icon]");
      if (trigger) trigger.click();
      else if (!win.location.pathname.endsWith("/cart")) win.location.assign(cartRoot + "cart");
    }

    doc.addEventListener("change", event => {
      if (!event.target.matches?.("[data-fang-toggle]")) return;
      if (!claim || preferenceFor(state, confirmedState).disabled) { render(); return; }
      if (doc.activeElement === event.target) toggleFocus = { element: event.target, section: event.target.closest("[data-section-type]") };
      saveClaim({ ...claim, declined: !event.target.checked });
      // Invalidate an in-flight confirmation before displaying its old result.
      lastMutation += 1;
      state = "updating";
      render();
      void sync();
    });

    doc.addEventListener("click", event => {
      if (event.target.closest?.("[data-fang-retry]")) { sync(); return; }
      const signup = event.target.closest?.('a[href="#signup"]');
      if (signup) {
        event.preventDefault();
        win._klOnsite = win._klOnsite || [];
        win._klOnsite.push(["openForm", config.formId]);
        return;
      }
      const link = event.target.closest?.('a[href*="fang_gift=claim"]');
      if (link && new URL(link.href).origin === win.location.origin) {
        event.preventDefault();
        const restored = claimFromUrl(link.href);
        if (restored) saveClaim(restoreClaim(claim, restored));
        sync().then(openCart);
      }
    });

    doc.addEventListener("submit", async event => {
      const form = event.target;
      if (allowSubmit || !claim || !(form instanceof HTMLFormElement) || !/\/cart\/?$/.test(new URL(form.action, win.location.origin).pathname)) return;
      const submitter = event.submitter;
      if (submitter?.name !== "checkout") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const noteInput = form.querySelector('[name="note"]');
      if (noteInput && noteInput.value !== noteInput.dataset.fangNote) {
        try { await withCartLock(() => request("cart/update.js", { note: noteInput.value })); }
        catch { state = "error"; render(); return; }
      }
      const ok = await sync();
      if (!ok) return;
      form.querySelectorAll('[name="note"]').forEach(input => { if (input.dataset.fangNote !== undefined) input.value = input.dataset.fangNote; });
      allowSubmit = true;
      form.requestSubmit(submitter);
      allowSubmit = false;
    }, true);

    win.addEventListener("storage", event => {
      if (event.key === STORAGE_KEY) { lastMutation += 1; saveClaim(readStored()); state = claim ? "updating" : "none"; render(); schedule(); }
    });
    win.addEventListener("pageshow", schedule);
    win.addEventListener("focus", schedule);
    doc.addEventListener("visibilitychange", () => { if (!doc.hidden) schedule(); });
    doc.addEventListener("shopify:cart:lines-update", event => { if (event.promise) event.promise.then(schedule, schedule); });
    doc.addEventListener("shopify:section:load", schedule);
    doc.addEventListener("kayoz:cart-view", schedule);
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.("[data-kayoz-fang-card]") || node.querySelector?.("[data-kayoz-fang-card]"))))) {
        render();
      }
    });
    doc.addEventListener("focusin", event => {
      if (toggleFocus && event.target !== toggleFocus.element) toggleFocus = null;
    });
    observer.observe(doc.body, { childList: true, subtree: true });

    const incoming = claimFromUrl(win.location.href);
    if (incoming) saveClaim(restoreClaim(claim, incoming));
    const shouldOpen = new URL(win.location.href).searchParams.get("fang_gift") === "claim";
    if (shouldOpen) {
      const cleaned = new URL(win.location.href);
      ["fang_gift", "fang_claim", "fang_issued", "fang_expires"].forEach(key => cleaned.searchParams.delete(key));
      win.history.replaceState(null, "", cleaned);
    }
    sync().then(() => { if (shouldOpen) openCart(); });
  }

  return { WINDOW_MS, STORAGE_KEY, GIFT_PROPERTY, CLAIM_PROPERTY, GIFT_VALUE, validateClaim, createClaim, claimFromAttributes, claimFromUrl, claimUrl, restoreClaim, submittedEmail, eligible, statusFor, preferenceFor, giftNote, nextChange, reconcile, start };
});
